import { SessionUser } from './../../utils/SessionUser';
import { blocklyStore } from '../blocklyStore';
import { AssembleCodeEnum } from '../BlocklyAframeBuilder';
import '../../helper/animationLoops';
import { AbstractBlock } from '../AbstractBlock';
import { CodeSetting } from './CodeSetting';
import { CameraCodeSetting } from './CameraCodeSetting';
import { BlockType } from '../../@types/BlockType';
import { GlobalImport } from '../../utils/GlobalImport';
const Blockly = GlobalImport.getBlockly();

export class SceneCodeSetting {
  private static hasCamera(subBlock: string): boolean {
    // console.log("hasCamera ",subBlock);
    const hasNomalCamera = subBlock.indexOf(`main-camera`) >= 0;
    // カメラがあるか？
    return hasNomalCamera;
  }

  private static getDefaultCameraCode(block_id: string): string {
    let defaultCameraAtt = CodeSetting.getAttributeCode('position', '0 1.7 4');
    defaultCameraAtt += CodeSetting.getAttributeCode('id', 'default_camera');
    defaultCameraAtt += CodeSetting.getAttributeCode('camera', '');
    defaultCameraAtt += CodeSetting.getAttributeCode('look-controls', 'pointerLockEnabled: false');
    const defaultCamera = CodeSetting.getElementCode(
      'a-entity',
      defaultCameraAtt,
      block_id,
      CameraCodeSetting.getCameraCursorCode('mouse', block_id)
    );

    let rigAtt = CodeSetting.getAttributeCode('movement-controls', '');
    rigAtt += CodeSetting.getAttributeCode('id', 'rig');
    rigAtt += CodeSetting.getAttributeCode('opacity', '0');
    const rig = CodeSetting.getElementCode('a-entity', rigAtt, block_id, defaultCamera);
    return rig;
  }

  private static isAR(subBlock: string, attribute: string): boolean {
    const hasARAttribute = attribute && attribute.indexOf(`arjs`) >= 0;
    const hasARCamera = subBlock.indexOf(`createElement("a-marker-camera")`) >= 0;
    return hasARAttribute || hasARCamera;
  }

  /**
   * JSでシーンを生成するコードを返す
   */
  static getSceneJSCode(
    attribute: string,
    subBlock: string,
    fragmentName: string,
    block_id: string
  ): string {
    // 関数が入っていないと関数コールがうまく機能しないので読み込む
    const functionBlockCode = blocklyStore.builder.getFunctionBlockCode();

    // カメラがあるかARならば、デフォルトカメラのコードはかかない。
    const defaultCamera =
      this.hasCamera(subBlock) || this.isAR(subBlock, attribute)
        ? ''
        : this.getDefaultCameraCode(block_id);

    // console.log('hasCamerae', CodeSetting.hasCamera(subBlock));

    // ARならVRボタンを出さない
    attribute += this.isAR(subBlock, attribute)
      ? CodeSetting.getAttributeCode('vr-mode-ui', 'enabled: false')
      : '';

    // console.log("functionBlockCode",functionBlockCode);
    const jsSceneCode = `
			/*最初は関数*/
			${functionBlockCode};

			/*フラグメントに溜め込み開始*/
		  window.parent = ${fragmentName}

			let entity = document.createElement("a-scene");
			entity.setAttribute("embedded", "");

			${attribute}
			parent.append(entity);
			let scene = entity;

			entity = document.createElement("a-assets");
			entity.append(document.createComment("assets_placeholder"));
			scene.append(entity);
			parent = scene;

			${subBlock}
			${defaultCamera}

		`;
    return jsSceneCode;
  }

  /**
   * シーンのコードを作成
   * @param {*} attribute
   * @param {*} subBlock
   */
  static getSceneCode(attribute: string, block: BlockType): string {
    const subBlock = CodeSetting.getSubstatementCode(block);

    // JSでのDOM生成コードを評価して、blockvrockVirtualDOM変数に貯める
    const blockvrockVirtualDOM = 'blockvrockVirtualDOM';
    const jsSceneCreateCode = this.getSceneJSCode(
      attribute,
      subBlock,
      blockvrockVirtualDOM,
      block.id
    );

    // vdom を作るコードを保持しておく
    blocklyStore.virtual_dom_js = `
      "use strict";
      let ${blockvrockVirtualDOM} = document.createDocumentFragment();
      ${jsSceneCreateCode};
      return ${blockvrockVirtualDOM};
    `;

    // JS評価実施！(evalより安全(現DOM環境を汚さない))
    let blockvrockVirtualDOMResult;
    try {
      blockvrockVirtualDOMResult = Function(blocklyStore.virtual_dom_js)();
    } catch (e) {
      console.error('DynamicDomScriptのパース失敗', e);
      console.error(blocklyStore.virtual_dom_js);
    }
    // フラグメントからHTMLを取り出す
    const htmlSceneCode = CodeSetting.convertFragmentToHTMLString(blockvrockVirtualDOMResult);

    // ベースのライブラリを取得
    const libs = AbstractBlock.useLibraryInWorkspace;

    const footer_contents = SessionUser.instance.isPro()
      ? ''
      : `<div id="footer" class="position-absolute bottom-0 left-0 w-75 mw-75 px-1 py-1" style="z-index: 800">
      <a href="https://blockvrock.com/" target="_blank" class="fs-6">${blocklyStore.title} by <b>BLOCKVROCK</b></a>
    </div>`;

    // 固定部分を付与
    const str = `
    <!DOCTYPE html>
    <html style="height:100%">
      <head>
        <!-- <script type="importmap">{"imports": {"three": "https://threejs.org/build/three.module.js"}}</script> -->
        ${libs
          .map((lib) => {
            const src = `<script type='text/javascript' src='${lib.libs}' ></script>`;
            return src;
          })
          .join(`\n`)}
        <meta charset = "UTF-8" >
        <link rel="stylesheet" href="../public/exec.css" />

        ${AssembleCodeEnum.HTML_INNER_PLACE_HOLER}
      </head>
      <body style="margin: 0; overflow: hidden; height:100%;">
        <script type="text/javascript">
        ${AssembleCodeEnum.FUNC_PLACE_HOLER}
        ready(async ()=>{
          let scene = document.querySelector("a-scene");
          let parent = scene;
          const oldConsole = window.console;
          const console = {log :null};
          console.log = window.developerConsole.getWriteFunction(oldConsole.log);
          console.error = window.developerConsole.getWriteFunction(oldConsole.error, "error");
          try{
            ${AssembleCodeEnum.JAVASCRIPT_PLACE_HOLDER};
            ${blocklyStore.js_code ? `${blocklyStore.js_code}` : ''};
          } catch(e){
            console.error(e);
          }
        });
        go();
        </script>
        ${htmlSceneCode}
        <script type="text/javascript">
        ${AssembleCodeEnum.JAVASCRIPT_AFTER_BODY_PLACE_HOLDER}
        </script>

        ${footer_contents}
      </body>
    </html>
    `;

    return str;
  }
}
